// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import { joyoKanji } from "./joyoKanji";
import { jinmeiyoKanji } from "./jinmeiyoKanji";
import { joyoKanjiMeaningsWithJlpt, allKanjiMeanings, totalKanjiCount } from "./allKanjiMeanings";

const ptNumber = (value) => value.toLocaleString("pt-PT");

export const blogPostContentPt = {
    "joyo-kanji-list": [
        {
            text:
                `Os kanji jōyō (常用, "de uso corrente") são os ${ptNumber(joyoKanji.length)} caracteres ` +
                "designados oficialmente pelo Ministério da Educação do Japão para uso em leis, jornais e " +
                "publicações do dia a dia, numa lista revista pela última vez em 2010. São ensinados ao longo da " +
                "escolaridade obrigatória e, em conjunto, são aquilo que se espera que um leitor adulto fluente " +
                "reconheça sem furigana.",
        },
        {
            text:
                "Ao contrário do hiragana e do katakana, os kanji representam significados e não apenas sons, e a " +
                "maioria tem várias leituras possíveis consoante o contexto. A lista abaixo junta cada kanji jōyō " +
                "a um dos seus significados mais comuns, para que a possa consultar como referência e não apenas " +
                "como uma parede de caracteres.",
        },
        { heading: `Todos os ${ptNumber(joyoKanji.length)} kanji, com significados` },
        {
            text:
                "Use os filtros abaixo para reduzir a lista a um único nível JLPT - prático se estiver a estudar " +
                "para um exame específico em vez de percorrer o conjunto todo de uma vez.",
        },
        { kanjiMeaningGrid: joyoKanjiMeaningsWithJlpt },
        {
            parts: [
                "Esta lista cobre apenas o significado, não a pronúncia - a maioria dos kanji jōyō tem uma leitura " +
                "on'yomi (de origem chinesa) e uma leitura kun'yomi (japonesa nativa), o que é uma coisa à parte " +
                "a aprender para cada um. Para uma introdução a como os kanji se encaixam com o hiragana e o " +
                "katakana, veja a ",
                { to: "/lessons", label: "página de lições" },
                ", ou experimente o ",
                { to: "/converter", label: "conversor de hiragana para kanji" },
                " para ver que kanji se usam habitualmente para escrever palavras do dia a dia. Para ver como os " +
                "kanji são construídos a partir de partes mais pequenas, consulte a ",
                { to: "/radicals", label: "referência de radicais" },
                ".",
            ],
        },
    ],
    "full-kanji-list": [
        {
            text:
                "Esta lista junta os dois conjuntos de kanji abrangidos por este site: os " +
                `${ptNumber(joyoKanji.length)} kanji jōyō (常用, "de uso corrente"), ensinados na escolaridade ` +
                `obrigatória e usados em toda a escrita do dia a dia, e os ${ptNumber(jinmeiyoKanji.length)} ` +
                'kanji jinmeiyō (人名用, "para uso em nomes próprios"), que estão aprovados para nomes mas não ' +
                "fazem parte do currículo normal.",
        },
        {
            text:
                "Juntos, correspondem a todos os kanji que encontra numa página de detalhe do NihongoTech. Como os " +
                "kanji jinmeiyō não saem no JLPT, só aparecem quando escolhe \"Todos\" abaixo, e não dentro de um " +
                "nível específico.",
        },
        { heading: `Todos os ${ptNumber(totalKanjiCount)} kanji, com significados` },
        {
            text:
                "Use os filtros abaixo para reduzir a lista a um único nível JLPT, ou veja tudo de uma vez com " +
                "\"Todos\".",
        },
        { kanjiMeaningGrid: allKanjiMeanings },
        {
            parts: [
                "Procura apenas os kanji jōyō? Veja a ",
                { to: "/blog/joyo-kanji-list", label: "lista de kanji jōyō" },
                ", ou vá à ",
                { to: "/lessons", label: "página de lições" },
                " para uma introdução a como os kanji se encaixam com o hiragana e o katakana. Quer saber como os " +
                "kanji são construídos a partir de partes mais pequenas? Consulte a ",
                { to: "/radicals", label: "referência de radicais" },
                ".",
            ],
        },
    ],
    "hiragana-vs-katakana": [
        {
            text:
                "Se está a começar a aprender japonês, as duas escritas kana podem parecer uma redundância " +
                "estranha: o hiragana (ひらがな) e o katakana (カタカナ) representam exatamente o mesmo conjunto " +
                "de sons, só com formas diferentes. No entanto, não são alternativas entre as quais se escolhe - " +
                "cada uma tem a sua função.",
        },
        { heading: "Distinga-os pela forma" },
        {
            text:
                "O hiragana é arredondado e fluido - あ, ひ, ら têm curvas e laçadas, porque esta escrita nasceu " +
                "da caligrafia cursiva. O katakana é anguloso e quadrado - ア, ヒ, ラ são feitos de traços curtos e " +
                "retos, porque cada caráter era originalmente apenas um fragmento de um kanji mais complexo. Depois " +
                "de ver alguns lado a lado, é fácil distinguir os dois estilos, mesmo antes de os conseguir ler.",
        },
        { heading: "Distinga-os pelo uso" },
        {
            list: [
                "O hiragana escreve palavras japonesas nativas, partículas gramaticais (は, を, に…) e terminações de verbos e adjetivos.",
                "O katakana escreve empréstimos de línguas estrangeiras (コーヒー, kōhī, \"café\"), nomes estrangeiros e onomatopeias.",
                "O katakana também serve para dar ênfase, de forma semelhante ao itálico em português.",
            ],
        },
        {
            text:
                "Uma regra prática simples: se uma palavra veio do inglês, do francês ou de outra língua " +
                "estrangeira há relativamente pouco tempo, quase de certeza que se escreve em katakana. Tudo o " +
                "resto - sobretudo a \"cola\" gramatical da frase - é hiragana.",
        },
        {
            parts: [
                "Como têm uma correspondência de um para um, depois de aprender o hiragana, aprender o katakana é " +
                "sobretudo memorizar um segundo conjunto de formas para sons que já conhece. Experimente as duas " +
                "escritas no teclado de prática, ou leia a explicação completa nas ",
                { to: "/lessons", label: "lições de hiragana e katakana" },
                ".",
            ],
        },
    ],
    "type-japanese-without-japanese-keyboard": [
        {
            text:
                "A maioria das pessoas que tenta escrever japonês pela primeira vez parte do princípio de que " +
                "precisa de instalar um editor de métodos de introdução (IME) ou mudar toda a disposição do " +
                "teclado. Não precisa - a forma habitual como os próprios japoneses escrevem kana num teclado " +
                "QWERTY normal é a introdução em romaji, e aqui funciona da mesma maneira.",
        },
        { heading: "Como funciona a introdução em romaji" },
        {
            text:
                "A introdução em romaji converte as letras latinas que escreve nos kana que têm o mesmo som. " +
                "Escreva \"ka\" e obtém か; escreva \"kya\" e obtém きゃ. Não é preciso decorar onde fica cada kana " +
                "numa disposição especial - está apenas a soletrar o som.",
        },
        {
            list: [
                "Vogais isoladas: a, i, u, e, o → あ, い, う, え, お",
                "Consoante + vogal: ka, shi, tsu, etc. → か, し, つ",
                "Sons combinados: kya, sho, ryu, etc. → きゃ, しょ, りゅ",
                "Consoantes dobradas: escreva a consoante duas vezes, p. ex. \"kitte\" → きって (o っ pequeno)",
                "O ん nasal: escreva \"n\" (ou \"nn\" antes de uma vogal, para não se juntar a na/ni/nu/ne/no)",
            ],
        },
        { heading: "Experimente diretamente no navegador" },
        {
            parts: [
                "O ",
                { to: "/keyboard", label: "teclado no ecrã" },
                " deste site faz exatamente isto: escreva romaji no campo de texto e veja-o converter-se em " +
                "hiragana ou katakana em tempo real, ou clique diretamente nos caracteres se preferir não escrever " +
                "romaji. Não há nada para instalar nem para configurar.",
            ],
        },
        {
            parts: [
                "Já tem o hiragana e quer o kanji com que normalmente se escreve? O ",
                { to: "/converter", label: "conversor de hiragana para kanji" },
                " sugere kanji comuns para vocabulário de iniciação à medida que escreve.",
            ],
        },
    ],
    "tips-to-memorize-kana-faster": [
        {
            text:
                "O hiragana e o katakana têm 46 caracteres cada, mais as formas sonoras e combinadas - mas, ao " +
                "contrário dos kanji, é um conjunto fixo e finito que só precisa de aprender uma vez. Eis como lá " +
                "chegar depressa.",
        },
        { heading: "1. Aprenda o hiragana todo antes de começar o katakana" },
        {
            text:
                "É tentador estudar os dois ao mesmo tempo, já que representam os mesmos sons, mas misturá-los " +
                "cedo só duplica a probabilidade de confundir caracteres parecidos. Consolide primeiro o hiragana " +
                "- depois, o katakana vai muito mais depressa, porque está só a aprender formas novas e não sons " +
                "novos.",
        },
        { heading: "2. Treine por linhas, não por ordem alfabética" },
        {
            text:
                "Aprenda primeiro as cinco vogais e depois uma linha de consoantes de cada vez (か・き・く・け・こ, " +
                "depois さ・し・す・せ・そ, e assim por diante). Isto reflete a forma como os caracteres estão " +
                "realmente organizados (a tabela gojūon) e torna as formas sonoras (が, ざ, だ…) triviais de " +
                "aprender a seguir, já que são as mesmas linhas com um dakuten acrescentado.",
        },
        { heading: "3. Escreva cada caráter à mão" },
        {
            parts: [
                "Escrever no teclado e ler são úteis, mas a memória muscular de desenhar mesmo um caráter - nem " +
                "que seja algumas vezes em papel - fixa-o muito mais depressa do que o simples reconhecimento. A " +
                "página de ",
                { to: "/kana-writing-practice", label: "prática de escrita de kana" },
                " anima a ordem dos traços de cada caráter hiragana e katakana e deixa-o traçá-lo por si.",
            ],
        },
        { heading: "4. Use mnemónicas para os que não ficam" },
        {
            text:
                "A maioria dos alunos aprende 80% dos kana depressa e depois fica presa num punhado de " +
                "caracteres parecidos (ぬ/め/ね, シ/ツ, ソ/ン). Uma imagem disparatada ligada à forma - \"o ン parece " +
                "que está prestes a cair\" - funciona melhor do que a repetição pura precisamente para estes " +
                "resistentes.",
        },
        { heading: "5. Pratique com escrita real, não só com cartões" },
        {
            parts: [
                "Reconhecer um caráter passivamente é uma competência diferente de o produzir. Escrever palavras " +
                "reais no ",
                { to: "/keyboard", label: "teclado de prática" },
                " - ou percorrer as ",
                { to: "/lessons", label: "lições" },
                " caráter a caráter - obriga a uma recordação ativa, que é o que realmente se transfere para a " +
                "leitura de texto japonês real.",
            ],
        },
    ],
    "translate-japanese-from-a-photo": [
        {
            text:
                "A forma mais rápida de traduzir japonês que não consegue escrever é normalmente uma fotografia - " +
                "uma ementa de restaurante, uma placa na rua, uma página de um livro ou uma captura de ecrã. O " +
                "tradutor do NihongoTech lê o texto japonês de uma fotografia diretamente no seu navegador e " +
                "depois traduz-o para inglês, sem ter de escrever um único caráter.",
        },
        { heading: "Traduzir uma fotografia, passo a passo" },
        {
            list: [
                "Abra a página do tradutor.",
                "Clique em \"Enviar uma fotografia em alternativa\" e escolha uma fotografia com texto japonês - uma captura " +
                "de ecrã funciona tão bem como uma fotografia tirada com uma câmara.",
                "Aguarde alguns segundos enquanto o texto é lido da imagem.",
                "Verifique o texto japonês extraído e corrija o que parecer errado antes de traduzir.",
                "Clique em \"Traduzir\" para obter a tradução em inglês.",
            ],
        },
        {
            parts: [
                "Experimente agora no ",
                { to: "/translate", label: "tradutor de fotografias em japonês" },
                " - é gratuito, e a fotografia nunca sai do seu navegador durante a leitura do texto.",
            ],
        },
        { heading: "Porque ler primeiro o texto, em vez de traduzir a fotografia diretamente" },
        {
            text:
                "Extrair o texto antes de o traduzir permite-lhe ver - e corrigir - exatamente o que foi lido, " +
                "antes de ser traduzido para inglês. O reconhecimento ótico de caracteres (OCR) pode ocasionalmente ler mal " +
                "um caráter numa fotografia desfocada, num tipo de letra invulgar ou em texto manuscrito, e " +
                "detetar isso na fase do texto evita uma tradução que parece segura mas está errada por uma razão " +
                "que nunca veria só pelo texto em inglês.",
        },
        { heading: "Tirar uma fotografia que se leia bem" },
        {
            list: [
                "Boa iluminação e uniforme - evite reflexos fortes ou sombras sobre o texto",
                "Segure a câmara mais ou menos paralela à página ou placa, não num ângulo acentuado",
                "Encha o enquadramento com o texto, em vez de muito fundo à volta",
                "O texto impresso lê-se com muito mais fiabilidade do que o manuscrito",
            ],
        },
        {
            parts: [
                "Já tem o texto escrito ou copiado algures? O mesmo ",
                { to: "/translate", label: "tradutor" },
                " aceita também texto japonês simples - o passo da fotografia existe apenas para texto que não " +
                "consegue escrever.",
            ],
        },
    ],
    "jlpt-europe-countries-with-no-test-site": [
        {
            text:
                "Os locais de exame do JLPT no estrangeiro mudam de ano para ano - um país pode perder o seu local " +
                "habitual, ou vê-lo suspenso durante uma sessão, sem grande aviso. Neste momento, há dois países " +
                "na Europa nessa situação: Portugal e a Bélgica. Se vive num deles, eis o que se passa de facto e " +
                "onde fica a alternativa real mais próxima.",
        },
        { heading: "Portugal: sem local ativo" },
        {
            text:
                "O local de exame JLPT indicado para Portugal está atualmente marcado como temporariamente " +
                "suspenso, sem nenhuma sessão agendada. O país mais próximo com locais reais e ativos é Espanha, " +
                "que realiza o exame em várias cidades todos os anos.",
        },
        { heading: "Bélgica: suspenso em 2026" },
        {
            text:
                "A Bélgica costuma realizar o JLPT em Lovaina e Gante, mas ambos os locais estão suspensos em " +
                "2026. A alternativa ativa mais próxima são os Países Baixos (Leiden) - mais perto do que as " +
                "opções seguintes, na Alemanha ou em França.",
        },
        { heading: "Encontrar automaticamente a opção mais próxima" },
        {
            parts: [
                "Em vez de verificar isto à mão, guarde o seu país na sua ",
                { to: "/profile", label: "página de perfil" },
                " e ele passa para a ",
                { to: "/jlpt", label: "pesquisa de locais de exame JLPT" },
                ": se o seu país não tiver atualmente nenhum local ativo, a página diz-lho claramente e sugere a " +
                "alternativa real mais próxima com um clique - sem alterar o país guardado no seu perfil, por " +
                "isso pode sempre pesquisar qualquer outro sítio.",
            ],
        },
        {
            text:
                "Esta lista reflete os dados dos locais à data da publicação e vai ficar desatualizada à medida " +
                "que os locais de exame mudarem - confirme sempre diretamente no site oficial do JLPT antes de " +
                "planear viagens.",
        },
    ],
    "romanization-systems": [
        {
            text:
                "A romanização é o processo de escrever uma língua que normalmente usa outra escrita - japonês, " +
                "chinês, coreano e muitas outras - com o alfabeto latino. 日本語 passa a nihongo, 你好 passa a " +
                "nǐ hǎo, 안녕하세요 passa a annyeonghaseyo. Nenhuma destas é uma tradução - as palavras continuam a " +
                "significar exatamente o que significavam. A romanização só muda a forma como os sons são " +
                "escritos, não o que querem dizer.",
        },
        {
            text:
                "As línguas adotam sistemas de romanização por algumas razões práticas: tornar a língua legível " +
                "para quem não conhece a escrita original, permitir escrever num teclado comum, uniformizar a " +
                "forma como nomes e lugares aparecem em mapas e passaportes a nível internacional, e dar aos " +
                "alunos uma ponte para a língua antes de dominarem o seu sistema de escrita verdadeiro.",
        },
        { heading: "Rōmaji: o japonês escrito com o alfabeto latino" },
        {
            parts: [
                "Em japonês, esta forma romanizada chama-se rōmaji (ローマ字, literalmente \"letras romanas\"). O " +
                "sistema que vai ver de longe com mais frequência - em placas de rua, em manuais e no ",
                { to: "/keyboard", label: "teclado de introdução em romaji" },
                " deste site - é a romanização Hepburn, criada no século XIX por James Curtis Hepburn. O Hepburn " +
                "foi pensado para corresponder à forma como os sons são de facto pronunciados por um falante de " +
                "inglês, e é precisamente por isso que é o padrão para alunos e para a maior parte do material " +
                "em inglês sobre o Japão.",
            ],
        },
        {
            text:
                "Existem ainda dois outros sistemas, embora os encontre muito menos vezes. O Kunrei-shiki é o " +
                "sistema oficialmente prescrito pelo governo japonês e ensinado nas escolas primárias do Japão; " +
                "segue a lógica da grelha de kana de forma mais rigorosa do que a pronúncia. O Nihon-shiki é um " +
                "sistema mais antigo e ainda mais fiel à grelha, no qual o Kunrei-shiki se baseou. Ambos existem " +
                "sobretudo para uso interno japonês e coerência linguística, e não para ajudar alunos " +
                "estrangeiros a ler a língua em voz alta - o que explica por que razão o Hepburn continua a ser " +
                "a escolha prática fora do Japão.",
        },
        { heading: "O mesmo kana, três grafias diferentes" },
        {
            text:
                "A forma mais clara de ver a diferença entre estes sistemas é um único carácter: し. No Hepburn, " +
                "escreve-se shi, tal como soa a um falante de inglês. No Kunrei-shiki e no Nihon-shiki, o mesmo " +
                "carácter escreve-se si, porque está na linha do \"s\" da grelha de kana, ao lado de さ (sa), す " +
                "(su), せ (se), そ (so) - coerente internamente, mas um guia pouco fiável para a pronúncia de " +
                "quem ainda não conhece a grelha.",
        },
        {
            list: [
                "ち é chi no Hepburn (mais uma vez, a corresponder à pronúncia)",
                "つ é tsu no Hepburn - ambos refletem sons consonânticos reais que os falantes de inglês já reconhecem",
                "ふ é fu no Hepburn, próximo do som real, que fica entre um \"f\" e um \"h\" ingleses",
            ],
        },
        {
            text:
                "É exatamente por isto que podem existir vários sistemas de romanização para uma só língua: cada " +
                "um otimiza uma coisa diferente. O Hepburn otimiza a pronúncia para quem vem de fora. O " +
                "Kunrei-shiki otimiza uma correspondência limpa, um para um, com a grelha de kana subjacente. " +
                "Nenhum está \"errado\" - foram apenas feitos para tarefas diferentes.",
        },
        { heading: "Vogais longas e macrons" },
        {
            text:
                "O japonês distingue vogais curtas e longas, e uma vogal longa é um som genuinamente diferente, " +
                "não apenas uma ênfase - おばさん (obasan, \"tia\") e おばあさん (obāsan, \"avó\") são palavras " +
                "diferentes. Na romanização Hepburn, uma vogal longa marca-se normalmente com um macron, o " +
                "pequeno traço sobre a letra: ā, ī, ū, ē, ō. É por isso que vê a capital do Japão escrita Tōkyō " +
                "em vez de Tokyo, e ありがとう escrito arigatō em vez de arigatou - o macron sobre o o final " +
                "representa o som longo de う que se segue. Os macrons omitidos são muito comuns na escrita " +
                "informal em inglês, mas não são mero enfeite - fazem parte de representar a palavra com rigor.",
        },
        { heading: "O rōmaji é uma ferramenta, não um substituto" },
        {
            parts: [
                "Convém ser direto: o rōmaji não é um dos verdadeiros sistemas de escrita do japonês. O texto " +
                "japonês real escreve-se com uma mistura de hiragana, katakana e kanji - nunca em rōmaji, " +
                "exceto em material de aprendizagem, sinalização para turistas ou logótipos. Se leva a sério a " +
                "leitura de japonês, o rōmaji é um degrau para usar no início e deixar largamente para trás, e " +
                "não um substituto a longo prazo para aprender de facto o ",
                { to: "/lessons", label: "hiragana e o katakana" },
                ". Todos os falantes nativos leem e escrevem em kana e kanji, nunca em letras latinas.",
            ],
        },
        {
            text:
                "Isto não quer dizer que o rōmaji seja inútil - longe disso. É genuinamente útil para escrever " +
                "japonês num teclado normal, para consultar a pronúncia antes de conseguir ler kana com " +
                "fluência e para acompanhar as primeiras lições. O objetivo é apenas tratá-lo como um andaime " +
                "que acaba por ser retirado, tal como as rodinhas de uma bicicleta.",
        },
        { heading: "Para lá do japonês: o Pinyin do mandarim" },
        {
            text:
                "O mandarim tem o seu próprio sistema de romanização muito usado, o Hanyu Pinyin (normalmente " +
                "só Pinyin), criado na China continental e hoje o padrão internacional. 你好 (\"olá\") passa a " +
                "nǐ hǎo, e 中国 (\"China\") passa a Zhōngguó. Repare nas pequenas marcas sobre as vogais em nǐ e " +
                "Zhōngguó - são marcas de tom, e não a mesma coisa que os macrons de vogal longa do japonês. O " +
                "mandarim é uma língua tonal, por isso o Pinyin tem de codificar com qual dos quatro tons uma " +
                "sílaba é pronunciada, já que a mesma sequência de letras pode significar coisas totalmente " +
                "diferentes consoante o tom.",
        },
        { heading: "O Jyutping do cantonês" },
        {
            text:
                "O cantonês, uma língua chinesa diferente falada sobretudo em Hong Kong, Macau e Guangdong, não " +
                "se romaniza com Pinyin - o Pinyin foi criado especificamente para os sons e tons do mandarim, " +
                "que não coincidem com os do cantonês. Em vez disso, um sistema comum é o Jyutping, criado pela " +
                "Linguistic Society of Hong Kong. Tal como o Pinyin, marca o tom, mas o cantonês tem mais tons " +
                "do que o mandarim, por isso o Jyutping representa-os com números a seguir a cada sílaba em vez " +
                "de acentos.",
        },
        { heading: "A Romanização Revista do coreano" },
        {
            text:
                "O coreano escreve-se na sua própria escrita, o Hangul, e não em caracteres chineses nem em " +
                "kana - mas também tem o seu sistema de romanização, exatamente pelas mesmas razões que o " +
                "japonês e o chinês. O sistema oficial atual na Coreia do Sul é a Romanização Revista do " +
                "Coreano, introduzida em 2000. Neste sistema, 한국어 (\"língua coreana\") passa a hangugeo, e " +
                "안녕하세요 (uma saudação comum) passa a annyeonghaseyo. Um sistema mais antigo, o " +
                "McCune-Reischauer, ainda é usado em alguns contextos académicos e na Coreia do Norte, e os dois " +
                "divergem em algumas grafias - mais um exemplo concreto de uma língua com mais do que um padrão " +
                "de romanização ativo em simultâneo.",
        },
        {
            text:
                "Este mesmo padrão repete-se na maioria das línguas que não usam nativamente o alfabeto latino - " +
                "o tailandês, o árabe, o cirílico do russo e muitas outras têm as suas convenções de " +
                "romanização estabelecidas, geralmente pela mesma mistura de razões: ajudar os estrangeiros a " +
                "pronunciar a língua, permitir a escrita num teclado normal e dar a sistemas internacionais, " +
                "como mapas e passaportes, uma forma consistente de representar nomes.",
        },
        { heading: "Romanização vs. transliteração vs. transcrição" },
        {
            text:
                "Estas três palavras usam-se de forma vaga e intermutável, mas descrevem ideias distintas. " +
                "Romanização significa especificamente converter uma escrita não latina para o alfabeto latino - " +
                "é a categoria ampla em que tudo o que acima se descreveu se enquadra. A transliteração é o " +
                "conceito mais lato: trocar os caracteres de uma escrita pelos de outra, geralmente procurando " +
                "preservar a grafia original o mais fielmente possível, letra a letra, mesmo que isso torne a " +
                "pronúncia menos óbvia. A transcrição dá prioridade a captar como uma palavra realmente soa, " +
                "usando os símbolos que melhor representam esses sons - um linguista que escreve uma palavra no " +
                "Alfabeto Fonético Internacional (IPA) está a transcrevê-la, e não a transliterá-la. O rōmaji " +
                "Hepburn aproxima-se mais da transcrição, por ser construído em torno da pronúncia real, " +
                "enquanto o Kunrei-shiki se aproxima mais da transliteração, por dar prioridade a um " +
                "mapeamento consistente e previsível a partir da grelha de kana, em vez da pronúncia exata.",
        },
        { heading: "Em resumo, para quem aprende japonês" },
        {
            parts: [
                "Use o rōmaji à vontade como principiante - para escrever, para ler palavras em voz alta, para " +
                "ler esta mesma frase sem saber ainda kana. Mas não deixe que se torne uma muleta que nunca " +
                "larga. Comece a aprender o ",
                { to: "/lessons", label: "hiragana e o katakana" },
                " o mais cedo possível e pratique a ",
                { to: "/kana-writing-practice", label: "escrevê-los à mão" },
                " para que a escrita verdadeira se torne tão automática como o alfabeto latino já é para si. O " +
                "rōmaji abriu-lhe a porta - a leitura a sério faz-se em kana e kanji.",
            ],
        },
    ],
};

export const blogPostPtTwins = {
    "learning-japanese-in-portugal-da-vinci-school": "aprender-japones-em-portugal-curso-n5",
    "hiragana-katakana-writing-practice-sheets": "fichas-hiragana-katakana-para-imprimir",
};
